import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Megaphone,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  Save,
  Loader2,
  Calendar,
  Building2,
  MapPin,
  Clock,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  RotateCw,
  Filter,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITY_BADGES = {
  urgent: { label: 'Urgent', bg: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  high: { label: 'High', bg: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  medium: { label: 'Medium', bg: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  low: { label: 'Low', bg: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-400' },
};

const STATUS_BADGES = {
  live: { label: 'Live Now', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  scheduled: { label: 'Scheduled', bg: 'bg-sky-50 text-sky-700 border-sky-200' },
  expired: { label: 'Expired', bg: 'bg-slate-100 text-slate-600 border-slate-200' },
  draft: { label: 'Draft', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  inactive: { label: 'Inactive', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const CompanyBroadcasts = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('all');
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form Data
  const defaultFormData = {
    title: '',
    content: '',
    priority: 'medium',
    company_id: 'all',
    company_name: 'All Companies',
    branch_id: '',
    branch_name: 'All Branches',
    publish_at: new Date().toISOString().slice(0, 16),
    start_at: new Date().toISOString().slice(0, 16),
    expiry_mode: 'duration', // 'duration' | 'date' | 'indefinite'
    display_duration_days: 7,
    expires_at: '',
    never_expires: false,
    is_published: true,
    is_active: true,
  };
  const [formData, setFormData] = useState(defaultFormData);

  // Fetch initial master lists
  useEffect(() => {
    fetchCompanies();
    fetchAnnouncements();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data || []);
    } catch (err) {
      console.warn('Failed to load companies:', err);
    }
  };

  const fetchBranches = useCallback(async (companyId) => {
    if (!companyId || companyId === 'all') {
      setBranches([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API_URL}/masters/locations?company_id=${companyId}`, {
        withCredentials: true,
      });
      setBranches(data || []);
    } catch (err) {
      console.warn('Failed to load branches:', err);
      setBranches([]);
    }
  }, []);

  const fetchAnnouncements = async (indicator = false) => {
    if (indicator) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/announcements`, { withCredentials: true });
      if (data && data.success) {
        setAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error('Failed to load announcements:', err);
      toast.error('Unable to load company broadcasts.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Open modal for new announcement
  const handleOpenCreateModal = () => {
    const nowIso = new Date().toISOString().slice(0, 16);
    setEditingId(null);
    setFormData({
      ...defaultFormData,
      publish_at: nowIso,
      start_at: nowIso,
    });
    setBranches([]);
    setShowModal(true);
  };

  // Open modal for editing
  const handleOpenEditModal = (item) => {
    setEditingId(item.id);
    let mode = 'duration';
    if (item.never_expires) {
      mode = 'indefinite';
    } else if (item.expires_at) {
      mode = 'date';
    }

    setFormData({
      title: item.title || '',
      content: item.content || item.description || '',
      priority: item.priority || 'medium',
      company_id: item.company_id || 'all',
      company_name: item.company_name || 'All Companies',
      branch_id: item.branch_id || '',
      branch_name: item.branch_name || 'All Branches',
      publish_at: item.publish_at ? item.publish_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
      start_at: item.start_at ? item.start_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
      expiry_mode: mode,
      display_duration_days: item.display_duration_days || 7,
      expires_at: item.expires_at ? item.expires_at.slice(0, 16) : '',
      never_expires: Boolean(item.never_expires),
      is_published: item.is_published !== false,
      is_active: item.is_active !== false,
    });

    if (item.company_id && item.company_id !== 'all') {
      fetchBranches(item.company_id);
    } else {
      setBranches([]);
    }

    setShowModal(true);
  };

  // Handle company change in modal
  const handleCompanyChange = (compVal) => {
    let compName = 'All Companies';
    if (compVal !== 'all') {
      const match = companies.find((c) => String(c.id) === String(compVal) || c.code === compVal);
      if (match) compName = match.name || match.company_name;
    }
    setFormData((prev) => ({
      ...prev,
      company_id: compVal,
      company_name: compName,
      branch_id: '',
      branch_name: 'All Branches',
    }));
    fetchBranches(compVal);
  };

  // Handle branch change in modal
  const handleBranchChange = (branchVal) => {
    let branchName = 'All Branches';
    if (branchVal && branchVal !== 'all') {
      const match = branches.find((b) => String(b.id || b._id) === String(branchVal) || b.code === branchVal);
      if (match) branchName = match.name || match.branch_name || match.location_name;
    }
    setFormData((prev) => ({
      ...prev,
      branch_id: branchVal === 'all' ? '' : branchVal,
      branch_name: branchName,
    }));
  };

  // Save / Submit announcement
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Announcement title is required.');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Announcement message/content is required.');
      return;
    }

    // Date & Expiry validation
    const startDate = new Date(formData.start_at);
    let expiresAt = null;
    let durationDays = null;
    let neverExpires = false;

    if (formData.expiry_mode === 'indefinite') {
      neverExpires = true;
    } else if (formData.expiry_mode === 'duration') {
      durationDays = parseInt(formData.display_duration_days, 10);
      if (!durationDays || durationDays <= 0) {
        toast.error('Please enter a valid display duration in days.');
        return;
      }
    } else if (formData.expiry_mode === 'date') {
      if (!formData.expires_at) {
        toast.error('Please select an expiry date and time.');
        return;
      }
      const expDate = new Date(formData.expires_at);
      if (expDate <= startDate) {
        toast.error('Expiry date and time cannot be earlier than start date and time.');
        return;
      }
      expiresAt = formData.expires_at;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        priority: formData.priority,
        company_id: formData.company_id || 'all',
        company_name: formData.company_name,
        branch_id: formData.branch_id || null,
        branch_name: formData.branch_name,
        publish_at: formData.publish_at,
        start_at: formData.start_at,
        expires_at: expiresAt,
        display_duration_days: durationDays,
        never_expires: neverExpires,
        is_published: formData.is_published,
        is_active: formData.is_active,
      };

      if (editingId) {
        await axios.put(`${API_URL}/announcements/${editingId}`, payload, { withCredentials: true });
        toast.success('Announcement updated successfully.');
      } else {
        await axios.post(`${API_URL}/announcements`, payload, { withCredentials: true });
        toast.success('Announcement broadcast created successfully.');
      }

      setShowModal(false);
      fetchAnnouncements(true);
    } catch (err) {
      console.error('Save announcement failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to save announcement.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle publish
  const handleTogglePublish = async (item) => {
    try {
      const nextVal = !item.is_published;
      await axios.patch(
        `${API_URL}/announcements/${item.id}/publish`,
        { value: nextVal },
        { withCredentials: true }
      );
      toast.success(nextVal ? 'Announcement published.' : 'Announcement unpublished.');
      fetchAnnouncements(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update publish state.');
    }
  };

  // Toggle active
  const handleToggleActive = async (item) => {
    try {
      const nextVal = !item.is_active;
      await axios.patch(
        `${API_URL}/announcements/${item.id}/activate`,
        { value: nextVal },
        { withCredentials: true }
      );
      toast.success(nextVal ? 'Announcement activated.' : 'Announcement deactivated.');
      fetchAnnouncements(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update active state.');
    }
  };

  // Delete announcement
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted successfully.');
      setDeleteConfirmId(null);
      fetchAnnouncements(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete announcement.');
    }
  };

  // Filtered Announcements
  const filteredAnnouncements = useMemo(() => {
    return announcements.filter((item) => {
      // Search
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchesTitle = (item.title || '').toLowerCase().includes(q);
        const matchesContent = (item.content || '').toLowerCase().includes(q);
        if (!matchesTitle && !matchesContent) return false;
      }
      // Company filter
      if (selectedCompanyFilter !== 'all') {
        if (item.company_id !== selectedCompanyFilter && item.company_id !== 'all') {
          return false;
        }
      }
      // Priority filter
      if (selectedPriorityFilter !== 'all') {
        if ((item.priority || '').toLowerCase() !== selectedPriorityFilter.toLowerCase()) {
          return false;
        }
      }
      // Status filter
      if (selectedStatusFilter !== 'all') {
        if (item.status !== selectedStatusFilter) {
          return false;
        }
      }
      return true;
    });
  }, [announcements, searchTerm, selectedCompanyFilter, selectedPriorityFilter, selectedStatusFilter]);

  return (
    <MainLayout>
      <div data-testid="company-broadcasts-page" className="space-y-6 max-w-7xl mx-auto pb-12">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600">
              <Megaphone className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
                Company Broadcasts & Announcements
              </h1>
              <p className="text-xs md:text-sm text-slate-500 mt-0.5">
                Manage live news tickers and announcements across companies, branches, and dashboards.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchAnnouncements(true)}
              disabled={refreshing}
              className="text-xs font-semibold gap-1.5"
            >
              <RotateCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleOpenCreateModal}
              data-testid="create-announcement-btn"
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white text-xs font-semibold gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              New Announcement
            </Button>
          </div>
        </div>

        {/* FILTERS BAR */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <div className="relative w-full max-w-sm">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search announcements by title or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Company Filter */}
            <div className="flex items-center gap-1.5 text-xs">
              <Building2 className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={selectedCompanyFilter}
                onChange={(e) => setSelectedCompanyFilter(e.target.value)}
                className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Companies</option>
                {companies.map((c) => (
                  <option key={c.id || c.code} value={c.id || c.code}>
                    {c.name || c.company_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div className="flex items-center gap-1.5 text-xs">
              <Filter className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={selectedPriorityFilter}
                onChange={(e) => setSelectedPriorityFilter(e.target.value)}
                className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-1.5 text-xs">
              <select
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                className="px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Statuses</option>
                <option value="live">Live Now</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired</option>
                <option value="draft">Draft (Unpublished)</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* ANNOUNCEMENTS TABLE */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
          {loading ? (
            <div className="p-12 text-center space-y-3">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto" />
              <p className="text-xs text-slate-500 font-medium">Loading broadcasts...</p>
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="p-12 text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto">
                <Megaphone className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-800">No broadcasts found</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {searchTerm || selectedCompanyFilter !== 'all' || selectedStatusFilter !== 'all'
                  ? 'No announcements match your search or filter criteria.'
                  : 'No announcements have been created yet. Click New Announcement to publish your first broadcast.'}
              </p>
              {announcements.length === 0 && (
                <Button
                  onClick={handleOpenCreateModal}
                  className="bg-[#0052FF] text-white text-xs mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" /> Create Announcement
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Status & Priority</th>
                    <th className="py-3 px-4">Announcement</th>
                    <th className="py-3 px-4">Target Audience</th>
                    <th className="py-3 px-4">Display Window</th>
                    <th className="py-3 px-4 text-center">Published</th>
                    <th className="py-3 px-4 text-center">Active</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAnnouncements.map((item) => {
                    const statusBadge = STATUS_BADGES[item.status] || STATUS_BADGES.live;
                    const priorityBadge = PRIORITY_BADGES[item.priority] || PRIORITY_BADGES.medium;

                    return (
                      <tr key={item.id} className="hover:bg-slate-50/70 transition-colors group">
                        {/* Status & Priority */}
                        <td className="py-3 px-4 align-top whitespace-nowrap space-y-1.5">
                          <div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusBadge.bg}`}>
                              {statusBadge.label}
                            </span>
                          </div>
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${priorityBadge.bg}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${priorityBadge.dot}`} />
                              {priorityBadge.label}
                            </span>
                          </div>
                        </td>

                        {/* Title & Content */}
                        <td className="py-3 px-4 align-top max-w-sm">
                          <p className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                            {item.title}
                          </p>
                          <p className="text-slate-500 line-clamp-2 mt-0.5 text-[11px] leading-relaxed">
                            {item.content || item.description}
                          </p>
                        </td>

                        {/* Target Audience */}
                        <td className="py-3 px-4 align-top whitespace-nowrap space-y-1">
                          <div className="flex items-center gap-1.5 text-slate-700 font-medium">
                            <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{item.company_name || 'All Companies'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-slate-500 text-[11px]">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>{item.branch_name || 'All Branches'}</span>
                          </div>
                        </td>

                        {/* Display Window */}
                        <td className="py-3 px-4 align-top whitespace-nowrap space-y-1">
                          <div className="flex items-center gap-1 text-slate-700 text-[11px]">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <span>Start: {item.start_at ? item.start_at.substring(0, 16).replace('T', ' ') : 'Immediate'}</span>
                          </div>
                          <div className="flex items-center gap-1 text-[11px]">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            {item.never_expires ? (
                              <span className="text-emerald-600 font-semibold">Never Expires</span>
                            ) : item.expires_at ? (
                              <span className="text-slate-600">Expires: {item.expires_at.substring(0, 16).replace('T', ' ')}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </div>
                        </td>

                        {/* Published Toggle */}
                        <td className="py-3 px-4 align-top text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePublish(item)}
                            title={item.is_published ? 'Click to unpublish' : 'Click to publish'}
                            className={`p-1.5 rounded-lg text-xs font-semibold transition ${
                              item.is_published
                                ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                            }`}
                          >
                            {item.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        </td>

                        {/* Active Toggle */}
                        <td className="py-3 px-4 align-top text-center">
                          <button
                            type="button"
                            onClick={() => handleToggleActive(item)}
                            title={item.is_active ? 'Click to deactivate' : 'Click to activate'}
                            className={`p-1.5 rounded-lg text-xs font-semibold transition ${
                              item.is_active
                                ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                : 'bg-rose-50 text-rose-500 hover:bg-rose-100'
                            }`}
                          >
                            {item.is_active ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                          </button>
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-4 align-top text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleOpenEditModal(item)}
                              data-testid={`edit-announcement-${item.id}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                              title="Edit Announcement"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(item.id)}
                              data-testid={`delete-announcement-${item.id}`}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition"
                              title="Delete Announcement"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CREATE / EDIT MODAL */}
        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto"
            onClick={() => setShowModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 my-8 animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-slate-900 text-white p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
                    <Megaphone className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">
                      {editingId ? 'Edit Announcement Broadcast' : 'Create Company Broadcast'}
                    </h2>
                    <p className="text-xs text-slate-400">
                      Configure title, message, schedule, and audience targeting.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto text-xs">
                {/* 1. Title */}
                <div>
                  <label className="block font-semibold text-slate-800 mb-1">
                    Announcement Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Annual Company Townhall Meeting 2026"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="announcement-title-input"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-medium text-slate-900 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                {/* 2. Message / Content */}
                <div>
                  <label className="block font-semibold text-slate-800 mb-1">
                    Message / Content <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Provide full announcement details, agenda, instructions, or links..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    data-testid="announcement-content-input"
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-normal text-slate-900 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                  />
                </div>

                {/* 3. Priority Selector */}
                <div>
                  <label className="block font-semibold text-slate-800 mb-1.5">
                    Priority Level
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { key: 'urgent', label: 'Urgent', color: 'peer-checked:bg-rose-50 peer-checked:border-rose-300 peer-checked:text-rose-700' },
                      { key: 'high', label: 'High', color: 'peer-checked:bg-amber-50 peer-checked:border-amber-300 peer-checked:text-amber-700' },
                      { key: 'medium', label: 'Medium', color: 'peer-checked:bg-blue-50 peer-checked:border-blue-300 peer-checked:text-blue-700' },
                      { key: 'low', label: 'Low', color: 'peer-checked:bg-slate-100 peer-checked:border-slate-300 peer-checked:text-slate-700' },
                    ].map((p) => (
                      <label
                        key={p.key}
                        className="cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="priority"
                          value={p.key}
                          checked={formData.priority === p.key}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                          className="sr-only peer"
                        />
                        <div className={`p-2 rounded-xl border border-slate-200 text-center font-bold text-slate-600 hover:bg-slate-50 transition-all ${p.color}`}>
                          {p.label}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 4. Company & Branch Audience Targeting */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                  <div>
                    <label className="block font-semibold text-slate-800 mb-1">
                      Target Company
                    </label>
                    <select
                      value={formData.company_id}
                      onChange={(e) => handleCompanyChange(e.target.value)}
                      data-testid="announcement-company-select"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">— All Companies (Tenant-wide) —</option>
                      {companies.map((c) => (
                        <option key={c.id || c.code} value={c.id || c.code}>
                          {c.name || c.company_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-800 mb-1">
                      Target Branch / Location (Optional)
                    </label>
                    <select
                      value={formData.branch_id || 'all'}
                      onChange={(e) => handleBranchChange(e.target.value)}
                      disabled={formData.company_id === 'all'}
                      data-testid="announcement-branch-select"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      <option value="all">— All Branches —</option>
                      {branches.map((b) => (
                        <option key={b.id || b._id || b.code} value={b.id || b._id || b.code}>
                          {b.name || b.branch_name || b.location_name}
                        </option>
                      ))}
                    </select>
                    {formData.company_id === 'all' && (
                      <p className="text-[10px] text-slate-400 mt-1">
                        Select a specific company first to enable branch-level targeting.
                      </p>
                    )}
                  </div>
                </div>

                {/* 5. Start and Publish Timestamps */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                  <div>
                    <label className="block font-semibold text-slate-800 mb-1">
                      Start Displaying Date & Time <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.start_at}
                      onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                      data-testid="announcement-start-at-input"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-800 mb-1">
                      Publish Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.publish_at}
                      onChange={(e) => setFormData({ ...formData, publish_at: e.target.value })}
                      data-testid="announcement-publish-at-input"
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* 6. Display Expiry Policy */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <label className="block font-bold text-slate-800">
                    Display Duration & Expiry Policy <span className="text-rose-500">*</span>
                  </label>

                  <div className="space-y-2">
                    {/* Option A: Duration in days */}
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="radio"
                        name="expiry_mode"
                        value="duration"
                        checked={formData.expiry_mode === 'duration'}
                        onChange={() => setFormData({ ...formData, expiry_mode: 'duration' })}
                        className="mt-0.5 text-indigo-600"
                      />
                      <div className="flex-1">
                        <span className="font-semibold text-slate-800">Display Duration (Days)</span>
                        <p className="text-[11px] text-slate-500">Automatically expires after specified number of days from start.</p>
                        {formData.expiry_mode === 'duration' && (
                          <div className="mt-2 flex items-center gap-2">
                            <select
                              value={formData.display_duration_days}
                              onChange={(e) => setFormData({ ...formData, display_duration_days: e.target.value })}
                              data-testid="announcement-duration-select"
                              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                            >
                              <option value={1}>1 Day</option>
                              <option value={3}>3 Days</option>
                              <option value={7}>7 Days (1 Week)</option>
                              <option value={14}>14 Days (2 Weeks)</option>
                              <option value={30}>30 Days (1 Month)</option>
                              <option value={60}>60 Days (2 Months)</option>
                              <option value={90}>90 Days (Quarter)</option>
                            </select>
                            <span className="text-slate-500 text-[11px]">days visibility</span>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Option B: Specific Expiry Date */}
                    <label className="flex items-start gap-2.5 cursor-pointer pt-2 border-t border-slate-200/60">
                      <input
                        type="radio"
                        name="expiry_mode"
                        value="date"
                        checked={formData.expiry_mode === 'date'}
                        onChange={() => setFormData({ ...formData, expiry_mode: 'date' })}
                        className="mt-0.5 text-indigo-600"
                      />
                      <div className="flex-1">
                        <span className="font-semibold text-slate-800">Specific Expiry Date & Time</span>
                        <p className="text-[11px] text-slate-500">Stops displaying immediately once the configured deadline is reached.</p>
                        {formData.expiry_mode === 'date' && (
                          <div className="mt-2">
                            <input
                              type="datetime-local"
                              value={formData.expires_at}
                              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                              data-testid="announcement-expires-at-input"
                              className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-medium focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Option C: Explicit Indefinite Policy */}
                    <label className="flex items-start gap-2.5 cursor-pointer pt-2 border-t border-slate-200/60">
                      <input
                        type="radio"
                        name="expiry_mode"
                        value="indefinite"
                        checked={formData.expiry_mode === 'indefinite'}
                        onChange={() => setFormData({ ...formData, expiry_mode: 'indefinite' })}
                        className="mt-0.5 text-indigo-600"
                      />
                      <div className="flex-1">
                        <span className="font-semibold text-slate-800">No Expiration (Indefinite Policy)</span>
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          ⚠️ Explicit acknowledgment: Announcement will remain permanently on ticker until manually archived or unpublished.
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* 7. Status Toggles */}
                <div className="flex items-center gap-6 pt-2 border-t border-slate-100">
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Publish Immediately</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>Active Status</span>
                  </label>
                </div>

                {/* Modal Actions */}
                <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="text-xs font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="save-announcement-btn"
                    className="bg-[#0052FF] hover:bg-[#0040CC] text-white text-xs font-semibold gap-1.5 shadow-xs"
                  >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {editingId ? 'Update Announcement' : 'Publish Announcement'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* DELETE CONFIRMATION MODAL */}
        {deleteConfirmId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
            onClick={() => setDeleteConfirmId(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4 border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                <Trash2 className="w-6 h-6" />
              </div>
              <div className="text-center">
                <h3 className="text-base font-bold text-slate-900">Delete Announcement?</h3>
                <p className="text-xs text-slate-500 mt-1">
                  This announcement will be removed from all dashboard tickers immediately.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirmId(null)}
                  className="flex-1 text-xs"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirmId)}
                  data-testid="confirm-delete-btn"
                  className="flex-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold"
                >
                  Confirm Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default CompanyBroadcasts;
